import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Clock, Users, ChefHat, Wine, ArrowLeft } from "lucide-react";
import { SEO } from "../components/SEO";
import { api } from "../lib/api";
import { BIZ } from "../lib/constants";

export default function RecipeDetail() {
    const { slug } = useParams();
    const [data, setData] = useState(null);
    const [err, setErr] = useState(false);

    useEffect(() => { api.recipe(slug).then(setData).catch(() => setErr(true)); }, [slug]);

    if (err) return <div className="ls-container py-32 text-center text-white">Recipe not found. <Link to="/recipes" className="link-underline" style={{ color: "var(--ls-gold)" }}>Browse all</Link></div>;
    if (!data) return <div className="ls-container py-32 text-center" style={{ color: "var(--ls-text-muted)" }}>Loading…</div>;

    const r = data.recipe;
    const totalMinutes = r.prep_minutes;
    const isoDur = `PT${totalMinutes}M`;
    const jsonLd = {
        "@context": "https://schema.org", "@type": "Recipe",
        name: r.name, image: r.cover_image_url, description: r.description,
        recipeYield: `${r.servings} serving${r.servings > 1 ? "s" : ""}`,
        recipeCategory: "Cocktail", recipeCuisine: "Cocktail",
        totalTime: isoDur, prepTime: isoDur,
        recipeIngredient: r.ingredients.map(i => `${i.amount} ${i.unit} ${i.name}`.trim()),
        recipeInstructions: r.instructions.map((step, idx) => ({ "@type": "HowToStep", position: idx + 1, text: step })),
        author: { "@type": "Organization", name: "Liquor Studio" },
        keywords: r.tags?.join(", "),
    };

    return (
        <>
            <SEO title={r.seo_title || `${r.name} Recipe`} description={r.seo_description || r.description} path={`/recipes/${r.slug}`} image={r.cover_image_url} jsonLd={jsonLd} />

            <section style={{ paddingTop: 120 }}>
                <div className="ls-container">
                    <Link to="/recipes" className="inline-flex items-center gap-2 text-sm" style={{ color: "var(--ls-text-secondary)" }} data-testid="back-to-recipes">
                        <ArrowLeft size={14} /> All recipes
                    </Link>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mt-8">
                        <div style={{ aspectRatio: "1/1", overflow: "hidden", borderRadius: 6, border: "1px solid var(--ls-border-strong)" }}>
                            <img src={r.cover_image_url} alt={r.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                        </div>

                        <div>
                            <span className="eyebrow">Cocktail</span>
                            <h1 className="font-display text-white mt-4" style={{ fontSize: "clamp(40px, 6vw, 64px)", fontWeight: 500, lineHeight: 1.05, letterSpacing: "-0.02em" }} data-testid="recipe-name">{r.name}</h1>
                            <p className="mt-5" style={{ color: "var(--ls-text-secondary)", fontSize: 17, lineHeight: 1.7 }}>{r.description}</p>

                            <div className="grid grid-cols-4 gap-4 mt-8">
                                <div className="text-center" style={{ background: "var(--ls-panel)", border: "1px solid var(--ls-border)", padding: 16, borderRadius: 4 }}>
                                    <Clock size={16} style={{ color: "var(--ls-gold)", margin: "0 auto" }} />
                                    <div className="font-bebas mt-2 text-white" style={{ fontSize: 22 }}>{r.prep_minutes}<span style={{ fontSize: 12 }}>m</span></div>
                                </div>
                                <div className="text-center" style={{ background: "var(--ls-panel)", border: "1px solid var(--ls-border)", padding: 16, borderRadius: 4 }}>
                                    <Users size={16} style={{ color: "var(--ls-gold)", margin: "0 auto" }} />
                                    <div className="font-bebas mt-2 text-white" style={{ fontSize: 22 }}>{r.servings}</div>
                                </div>
                                <div className="text-center" style={{ background: "var(--ls-panel)", border: "1px solid var(--ls-border)", padding: 16, borderRadius: 4 }}>
                                    <ChefHat size={16} style={{ color: "var(--ls-gold)", margin: "0 auto" }} />
                                    <div className="font-bebas mt-2 text-white" style={{ fontSize: 14 }}>{r.difficulty}</div>
                                </div>
                                <div className="text-center" style={{ background: "var(--ls-panel)", border: "1px solid var(--ls-border)", padding: 16, borderRadius: 4 }}>
                                    <Wine size={16} style={{ color: "var(--ls-gold)", margin: "0 auto" }} />
                                    <div className="font-bebas mt-2 text-white" style={{ fontSize: 14 }}>{r.glassware}</div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mt-16">
                        <div>
                            <h2 className="font-display text-white mb-6" style={{ fontSize: 28, fontWeight: 500 }}>Ingredients</h2>
                            <ul className="space-y-3" data-testid="recipe-ingredients">
                                {r.ingredients.map((i, idx) => (
                                    <li key={idx} className="flex items-start justify-between gap-4 pb-3" style={{ borderBottom: "1px solid var(--ls-border)" }}>
                                        <div>
                                            {i.product_slug ? (
                                                <Link to={`/spirits/${i.product_slug}`} className="link-underline text-white" data-testid={`ingredient-link-${i.product_slug}`}>{i.name}</Link>
                                            ) : (
                                                <span className="text-white">{i.name}</span>
                                            )}
                                        </div>
                                        <span className="font-bebas" style={{ fontSize: 18, color: "var(--ls-gold)", whiteSpace: "nowrap" }}>{i.amount} {i.unit}</span>
                                    </li>
                                ))}
                            </ul>
                            {r.garnish && <p className="mt-6 text-sm" style={{ color: "var(--ls-text-secondary)" }}><strong className="text-white">Garnish:</strong> {r.garnish}</p>}
                        </div>

                        <div>
                            <h2 className="font-display text-white mb-6" style={{ fontSize: 28, fontWeight: 500 }}>Method</h2>
                            <ol className="space-y-5" data-testid="recipe-instructions">
                                {r.instructions.map((step, idx) => (
                                    <li key={idx} className="flex gap-4">
                                        <span className="font-bebas flex-shrink-0" style={{ fontSize: 28, color: "var(--ls-gold)", lineHeight: 1 }}>{String(idx + 1).padStart(2, "0")}</span>
                                        <p style={{ color: "var(--ls-text-secondary)", fontSize: 16, lineHeight: 1.7 }}>{step}</p>
                                    </li>
                                ))}
                            </ol>
                        </div>
                    </div>

                    {r.ingredients.some(i => i.product_slug) && (
                        <div className="mt-16 p-8" style={{ background: "var(--ls-panel)", border: "1px solid var(--ls-gold)", borderRadius: 6 }}>
                            <h3 className="font-display text-white" style={{ fontSize: 22, fontWeight: 500 }}>Get the spirits</h3>
                            <p className="mt-2 text-sm" style={{ color: "var(--ls-text-secondary)" }}>Available now at Liquor Studio, {BIZ.address.full}.</p>
                            <div className="flex flex-wrap gap-3 mt-5">
                                {r.ingredients.filter(i => i.product_slug).map(i => (
                                    <Link key={i.product_slug} to={`/spirits/${i.product_slug}`} className="btn-outline-gold" style={{ fontSize: 12, padding: "8px 16px" }} data-testid={`recipe-get-${i.product_slug}`}>
                                        {i.name.split("(")[0].trim()} →
                                    </Link>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </section>
        </>
    );
}
